/* ============================================================
   The opening.

   The mark is four opaque slabs, each with a lit face and a darker
   side wall. This is that construction at architectural scale and
   pulled apart: six slabs holding a stack they are not quite touching,
   with a handful of blocks at other depths keeping them company.

   Nothing is a picture of anything. There is no floor, no racking, no
   vehicle — the diagrams downstairs do that work, and the opening
   should be the shape of the company rather than a drawing of its
   product.

   Everything drifts, and nothing drifts together. Each block runs
   three sine waves — one per axis — at periods between twenty-four
   and forty seconds, none of them a multiple of another, so the
   composition never repeats a pose and never resolves into a beat you
   could tap along to. Amplitudes are a sixth of a block's own width
   and rotation stays under two degrees: at any one glance it is a
   still image, and only looking twice tells you it moved.
   ============================================================ */

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import type { Stage } from './stage.ts';

/* Near-neutral surfaces; the brand arrives as light. Lifted from the
   stylesheet's own argument about the mark, one step brighter than the
   floors downstairs because nothing here has a deck to sit against. */
const P = {
  base: 0x2e3a52,
  low: 0x3d4d6d,
  mid: 0x51648b,
  high: 0x6a7ea6,
  turq: 0x5eead4,
  blue: 0x3b82f6,
} as const;

const surfaces = new Map<number, THREE.MeshStandardMaterial>();

function surface(color: number) {
  let made = surfaces.get(color);
  if (!made) {
    made = new THREE.MeshStandardMaterial({ color, roughness: 0.42, metalness: 0.2 });
    surfaces.set(color, made);
  }
  return made;
}

/** A lit face. Emissive stays low on purpose — much past 0.3 and the tone
 *  map turns turquoise into white, which is a different colour entirely. */
const lamp = (color: number, strength = 0.3) =>
  new THREE.MeshStandardMaterial({
    color: new THREE.Color(color).multiplyScalar(0.34),
    emissive: color,
    emissiveIntensity: strength,
    roughness: 0.4,
    metalness: 0,
  });

type Block = {
  mesh: THREE.Mesh;
  home: THREE.Vector3;
  amp: THREE.Vector3;
  rate: THREE.Vector3;
  phase: THREE.Vector3;
  spin: number;
  rest: number;
};

const blocks: Block[] = [];

/** One block, and its own private clock.
 *
 *  `slow` scales the periods: the big slabs of the stack move at half the
 *  rate of the loose blocks around them, which is what stops the whole thing
 *  reading as one object wobbling. */
function block(parent: THREE.Object3D, w: number, h: number, d: number, at: [number, number, number], material: THREE.Material, slow = 1) {
  const mesh = new THREE.Mesh(new RoundedBoxGeometry(w, h, d, 3, Math.min(0.09, Math.min(w, h, d) * 0.22)), material);
  mesh.position.set(...at);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);

  const drift = Math.max(w, d) * 0.16;
  const index = blocks.length;
  blocks.push({
    mesh,
    home: mesh.position.clone(),
    amp: new THREE.Vector3(drift, drift * 0.68, drift),
    // Mutually irrational-ish periods, so no two blocks ever line up twice.
    rate: new THREE.Vector3(
      (Math.PI * 2) / (29 * slow + index * 1.7),
      (Math.PI * 2) / (24 * slow + index * 2.3),
      (Math.PI * 2) / (37 * slow + index * 1.1),
    ),
    phase: new THREE.Vector3(index * 1.9, index * 2.7, index * 0.8),
    spin: (index % 2 ? 1 : -1) * 0.026,        // ±1.5°
    rest: mesh.rotation.y,
  });
  return mesh;
}

export function buildHero(stage: Stage) {
  blocks.length = 0;
  const root = stage.root;

  // The stack. Footprints narrow going up and the gaps between them are
  // deliberate: these are separate things held in one order, not a solid.
  const stackSpec: [number, number, number, number][] = [
    //  w     h     d     y
    [10.4, 1.5, 8.6, 0],
    [8.8, 1.25, 7.3, 2.15],
    [7.3, 1.1, 6.1, 4.0],
    [5.9, 0.95, 4.9, 5.65],
    [4.6, 0.8, 3.8, 7.1],
  ];
  const shades = [P.base, P.low, P.mid, P.high, P.high];
  stackSpec.forEach(([w, h, d, y], i) => {
    block(root, w, h, d, [0, y, 0], surface(shades[i]!), 2);
  });

  // The top slab is the lit one — the mark's own crown, and the only
  // saturated thing in the frame.
  block(root, 3.4, 0.62, 2.8, [0, 8.35, 0], lamp(P.turq, 0.32), 2);

  // Company. Big enough to read as siblings of the stack rather than as
  // debris, spread across depth so the field has somewhere to recede to.
  const satellites: [number, number, number, number, number][] = [
    //  size   x      y      z    shade
    [4.2, -10.6, 2.6, 3.4, P.low],
    [3.0, 9.2, 6.8, -2.8, P.mid],
    [4.8, 8.2, -2.4, 6.2, P.base],
    [2.4, -7.4, 9.2, -4.6, P.high],
    [3.6, -12.8, -3.4, -3.2, P.base],
    [2.8, 12.4, 1.6, 1.8, P.low],
  ];
  for (const [s, x, y, z, shade] of satellites) {
    block(root, s, s * 0.82, s * 0.94, [x, y, z], surface(shade));
  }

  // Two lit chips, the only blue in the frame, placed off the stack's axis so
  // the eye has a reason to travel out to the edges.
  block(root, 0.9, 0.34, 0.9, [-8.2, 6.6, -1.8], lamp(P.blue, 0.34));
  block(root, 0.7, 0.28, 0.7, [9.8, -2.4, 3.2], lamp(P.turq, 0.28));

  stage.fit(0.86, [-7.4, 0.2]);

  stage.onTick((t) => {
    for (const b of blocks) {
      b.mesh.position.set(
        b.home.x + Math.sin(t * b.rate.x + b.phase.x) * b.amp.x,
        b.home.y + Math.sin(t * b.rate.y + b.phase.y) * b.amp.y,
        b.home.z + Math.sin(t * b.rate.z + b.phase.z) * b.amp.z,
      );
      b.mesh.rotation.y = b.rest + Math.sin(t * b.rate.z * 0.6 + b.phase.y) * b.spin;
    }
    root.rotation.y = Math.sin(t * 0.045) * 0.02;
  });
}
